/**
 * Ingredient consumption, food cost and usage reports.
 * Everything is derived from the automatic consumption log written when the
 * kitchen marks an order ready — no separate recipe module to maintain.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowDownWideNarrow, ArrowUpWideNarrow, IndianRupee, Layers, Utensils } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import {
  AnalyticsFilters,
  ChartFrame,
  DataTable,
  KpiCard,
  Panel,
  TONE_VAR,
  compactInr,
  exportCsv,
  exportExcel,
  inr,
  num,
  tooltipStyle,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { resolveSuiteWindow, type SuiteRangeKey } from "@/lib/admin/analytics-range";
import { formatPaise } from "@/lib/format/money";
import { getConsumptionReports, getRecipe } from "@/lib/admin/recipes.functions";
import { getInventoryReportExtras } from "@/lib/admin/inventory-reports.functions";
import type {
  DishMargin,
  ExpensiveIngredient,
  IngredientDetail,
  LowStockImpact,
  WasteRow,
} from "@/lib/admin/inventory-reports.data";
import { RECIPE_KIND_LABEL, type RecipeKind } from "@/lib/admin/recipes.schemas";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_admin/admin/inventory-reports")({
  head: () => ({
    meta: [
      { title: "Inventory Reports — Admin" },
      {
        name: "description",
        content:
          "Ingredient consumption, food cost per portion and most or least used ingredients for the selected period.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InventoryReportsPage,
});


type ItemRow = {
  item_kind: string;
  item_title: string;
  portions: number;
  cost_paise: number;
  cost_per_portion_paise: number;
};

function InventoryReportsPage() {
  const [range, setRange] = useState<SuiteRangeKey>("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const win = useMemo(() => resolveSuiteWindow({ range, from, to }), [range, from, to]);
  const fetchReports = useServerFn(getConsumptionReports);
  const fetchExtras = useServerFn(getInventoryReportExtras);
  const fetchRecipe = useServerFn(getRecipe);
  const [breakdown, setBreakdown] = useState<DishMargin | null>(null);

  const q = useQuery({
    queryKey: ["admin", "inventory", "consumption-reports", win.from, win.to],
    queryFn: () => fetchReports({ data: { from: win.from, to: win.to } }),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });

  const ex = useQuery({
    queryKey: ["admin", "inventory", "report-extras", win.from, win.to],
    queryFn: () => fetchExtras({ data: { from: win.from, to: win.to } }),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });

  const recipeQ = useQuery({
    queryKey: ["admin", "inventory", "recipe-breakdown", breakdown?.item_id ?? ""],
    queryFn: () =>
      fetchRecipe({
        data: { kind: breakdown!.item_kind as RecipeKind, itemId: breakdown!.item_id },
      }),
    enabled: Boolean(breakdown?.item_id),
  });

  const data = q.data;
  const loading = q.isPending;
  const ingredients: IngredientDetail[] = ex.data?.ingredients ?? [];
  const items: ItemRow[] = data?.by_item ?? [];
  const dishes: DishMargin[] = ex.data?.dishes ?? [];
  const expensive: ExpensiveIngredient[] = ex.data?.expensive ?? [];
  const waste: WasteRow[] = ex.data?.waste ?? [];
  const lowStock: LowStockImpact[] = ex.data?.low_stock ?? [];
  const leastUsed = useMemo(() => [...ingredients].reverse().slice(0, 10), [ingredients]);

  const REORDER_LABEL: Record<IngredientDetail["reorder_status"], string> = {
    ok: "In stock",
    reorder: "Reorder soon",
    low: "Low",
    out: "Out of stock",
  };
  const REORDER_CLASS: Record<IngredientDetail["reorder_status"], string> = {
    ok: "text-muted-foreground",
    reorder: "text-amber-600",
    low: "text-amber-600",
    out: "text-destructive",
  };

  const ingredientColumns: Array<DataColumn<IngredientDetail>> = [
    {
      key: "name",
      header: "Ingredient",
      render: (r) => <span className="font-medium">{r.inventory_item_name}</span>,
      sortValue: (r) => r.inventory_item_name,
      exportValue: (r) => r.inventory_item_name,
    },
    {
      key: "category",
      header: "Category",
      render: (r) => r.category,
      sortValue: (r) => r.category,
      exportValue: (r) => r.category,
    },
    {
      key: "qty",
      header: "Quantity used",
      align: "right",
      render: (r) => num(r.quantity),
      sortValue: (r) => r.quantity,
      exportValue: (r) => r.quantity,
    },
    {
      key: "unit",
      header: "Unit",
      render: (r) => r.unit,
      sortValue: (r) => r.unit,
      exportValue: (r) => r.unit,
    },
    {
      key: "cost",
      header: "Food cost",
      align: "right",
      render: (r) => formatPaise(r.cost_paise),
      sortValue: (r) => r.cost_paise,
      exportValue: (r) => r.cost_paise / 100,
    },
    {
      key: "events",
      header: "Deductions",
      align: "right",
      render: (r) => num(r.events),
      sortValue: (r) => r.events,
      exportValue: (r) => r.events,
    },
    {
      key: "avg",
      header: "Avg daily usage",
      align: "right",
      render: (r) => `${num(r.avg_daily_qty)} ${r.unit}`,
      sortValue: (r) => r.avg_daily_qty,
      exportValue: (r) => r.avg_daily_qty,
    },
    {
      key: "stock",
      header: "Current stock",
      align: "right",
      render: (r) => `${num(r.current_stock)} ${r.unit}`,
      sortValue: (r) => r.current_stock,
      exportValue: (r) => r.current_stock,
    },
    {
      key: "status",
      header: "Reorder status",
      render: (r) => (
        <span className={REORDER_CLASS[r.reorder_status]}>{REORDER_LABEL[r.reorder_status]}</span>
      ),
      sortValue: (r) => r.reorder_status,
      exportValue: (r) => REORDER_LABEL[r.reorder_status],
    },
  ];

  const dishColumns: Array<DataColumn<DishMargin>> = [
    {
      key: "dish",
      header: "Dish",
      render: (r) => <span className="font-medium">{r.item_title || "—"}</span>,
      sortValue: (r) => r.item_title,
      exportValue: (r) => r.item_title,
    },
    {
      key: "food",
      header: "Food cost / portion",
      align: "right",
      render: (r) => formatPaise(r.cost_per_portion_paise),
      sortValue: (r) => r.cost_per_portion_paise,
      exportValue: (r) => r.cost_per_portion_paise / 100,
    },
    {
      key: "price",
      header: "Selling price",
      align: "right",
      render: (r) => (r.selling_price_paise === null ? "—" : formatPaise(r.selling_price_paise)),
      sortValue: (r) => r.selling_price_paise ?? 0,
      exportValue: (r) => (r.selling_price_paise ?? 0) / 100,
    },
    {
      key: "margin",
      header: "Gross margin",
      align: "right",
      render: (r) => (r.gross_margin_paise === null ? "—" : formatPaise(r.gross_margin_paise)),
      sortValue: (r) => r.gross_margin_paise ?? 0,
      exportValue: (r) => (r.gross_margin_paise ?? 0) / 100,
    },
    {
      key: "pct",
      header: "Margin %",
      align: "right",
      render: (r) => (r.margin_pct === null ? "—" : `${r.margin_pct}%`),
      sortValue: (r) => r.margin_pct ?? 0,
      exportValue: (r) => r.margin_pct ?? 0,
    },
    {
      key: "view",
      header: "",
      render: (r) => (
        <Button variant="ghost" size="sm" onClick={() => setBreakdown(r)}>
          Breakdown
        </Button>
      ),
      exportValue: () => "",
    },
  ];

  const expensiveColumns: Array<DataColumn<ExpensiveIngredient>> = [
    {
      key: "name",
      header: "Ingredient",
      render: (r) => <span className="font-medium">{r.inventory_item_name}</span>,
      sortValue: (r) => r.inventory_item_name,
      exportValue: (r) => r.inventory_item_name,
    },
    {
      key: "price",
      header: "Purchase price",
      align: "right",
      render: (r) => `${formatPaise(r.purchase_price_paise)} / ${r.unit}`,
      sortValue: (r) => r.purchase_price_paise,
      exportValue: (r) => r.purchase_price_paise / 100,
    },
    {
      key: "qty",
      header: "Consumption",
      align: "right",
      render: (r) => `${num(r.monthly_quantity)} ${r.unit}`,
      sortValue: (r) => r.monthly_quantity,
      exportValue: (r) => r.monthly_quantity,
    },
    {
      key: "spend",
      header: "Spend",
      align: "right",
      render: (r) => formatPaise(r.monthly_spend_paise),
      sortValue: (r) => r.monthly_spend_paise,
      exportValue: (r) => r.monthly_spend_paise / 100,
    },
  ];

  const wasteColumns: Array<DataColumn<WasteRow>> = [
    {
      key: "name",
      header: "Ingredient",
      render: (r) => <span className="font-medium">{r.inventory_item_name}</span>,
      sortValue: (r) => r.inventory_item_name,
      exportValue: (r) => r.inventory_item_name,
    },
    {
      key: "qty",
      header: "Wasted",
      align: "right",
      render: (r) => `${num(r.waste_qty)} ${r.unit}`,
      sortValue: (r) => r.waste_qty,
      exportValue: (r) => r.waste_qty,
    },
    {
      key: "pct",
      header: "Waste %",
      align: "right",
      render: (r) => `${r.waste_pct}%`,
      sortValue: (r) => r.waste_pct,
      exportValue: (r) => r.waste_pct,
    },
    {
      key: "cost",
      header: "Waste cost",
      align: "right",
      render: (r) => formatPaise(r.waste_cost_paise),
      sortValue: (r) => r.waste_cost_paise,
      exportValue: (r) => r.waste_cost_paise / 100,
    },
    {
      key: "reason",
      header: "Reason",
      render: (r) => r.reason,
      sortValue: (r) => r.reason,
      exportValue: (r) => r.reason,
    },
  ];

  const lowStockColumns: Array<DataColumn<LowStockImpact>> = [
    {
      key: "name",
      header: "Ingredient",
      render: (r) => <span className="font-medium">{r.inventory_item_name}</span>,
      sortValue: (r) => r.inventory_item_name,
      exportValue: (r) => r.inventory_item_name,
    },
    {
      key: "stock",
      header: "Current stock",
      align: "right",
      render: (r) => `${num(r.current_stock)} ${r.unit}`,
      sortValue: (r) => r.current_stock,
      exportValue: (r) => r.current_stock,
    },
    {
      key: "reorder",
      header: "Reorder level",
      align: "right",
      render: (r) => `${num(r.reorder_level)} ${r.unit}`,
      sortValue: (r) => r.reorder_level,
      exportValue: (r) => r.reorder_level,
    },
    {
      key: "dishes",
      header: "Dishes affected",
      render: (r) => r.dishes.join(", ") || "—",
      sortValue: (r) => r.dishes.length,
      exportValue: (r) => r.dishes.join(" | "),
    },
    {
      key: "portions",
      header: "Portions remaining",
      align: "right",
      render: (r) => num(r.portions_remaining),
      sortValue: (r) => r.portions_remaining,
      exportValue: (r) => r.portions_remaining,
    },
  ];

  const breakdownLines = recipeQ.data?.lines ?? [];
  const breakdownTotal = breakdownLines.reduce((sum, l) => sum + l.line_cost_paise, 0);


  const itemColumns: Array<DataColumn<ItemRow>> = [
    {
      key: "title",
      header: "Item",
      render: (r) => <span className="font-medium">{r.item_title || "—"}</span>,
      sortValue: (r) => r.item_title,
      exportValue: (r) => r.item_title,
    },
    {
      key: "kind",
      header: "Type",
      render: (r) => RECIPE_KIND_LABEL[r.item_kind as RecipeKind] ?? r.item_kind,
      sortValue: (r) => r.item_kind,
      exportValue: (r) => r.item_kind,
    },
    {
      key: "portions",
      header: "Portions",
      align: "right",
      render: (r) => num(r.portions),
      sortValue: (r) => r.portions,
      exportValue: (r) => r.portions,
    },
    {
      key: "cost",
      header: "Food cost",
      align: "right",
      render: (r) => formatPaise(r.cost_paise),
      sortValue: (r) => r.cost_paise,
      exportValue: (r) => r.cost_paise / 100,
    },
    {
      key: "per",
      header: "Cost / portion",
      align: "right",
      render: (r) => formatPaise(r.cost_per_portion_paise),
      sortValue: (r) => r.cost_per_portion_paise,
      exportValue: (r) => r.cost_per_portion_paise / 100,
    },
  ];

  const exportRows = () =>
    ingredients.map((r) => ({
      Ingredient: r.inventory_item_name,
      Quantity: r.quantity,
      Unit: r.unit,
      Cost: r.cost_paise / 100,
      Deductions: r.events,
    }));

  return (
    <div>
      <AdminPageHeader
        title="Inventory Reports"
        description="Ingredient consumption, food cost per portion and most or least used ingredients, straight from kitchen activity."
      />
      <RefreshBar
        targets={[{ key: "inventory", label: "reports" }]}
        busy={q.isFetching}
        lastUpdated={q.dataUpdatedAt || null}
      />

      <AnalyticsFilters
        range={range}
        onRangeChange={setRange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        status={loading ? "Loading…" : `${win.label} · ${win.from} → ${win.to}`}
        onExportCsv={() => exportCsv("ingredient-consumption", exportRows())}
        onExportExcel={() => exportExcel("ingredient-consumption", exportRows())}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Food cost"
          value={compactInr((data?.totals.consumption_cost_paise ?? 0) / 100)}
          tone="warn"
          icon={IndianRupee}
          loading={loading}
          hint={win.label}
        />
        <KpiCard
          label="Portions produced"
          value={num(data?.totals.portions ?? 0)}
          tone="info"
          icon={Utensils}
          loading={loading}
          hint={`${num(data?.totals.orders ?? 0)} orders`}
        />
        <KpiCard
          label="Ingredients used"
          value={num(data?.totals.ingredients_used ?? 0)}
          tone="teal"
          icon={Layers}
          loading={loading}
          hint="Distinct stock items"
        />
        <KpiCard
          label="Average cost / portion"
          value={compactInr(
            ((data?.totals.portions ?? 0) > 0
              ? (data?.totals.consumption_cost_paise ?? 0) / (data?.totals.portions ?? 1)
              : 0) / 100,
          )}
          tone="success"
          icon={IndianRupee}
          loading={loading}
          hint="Ingredient cost only"
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Panel title="Daily consumption cost" subtitle={win.label}>
          <ChartFrame loading={loading} empty={(data?.daily.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(data?.daily ?? []).map((d) => ({ date: d.date, value: d.cost_paise / 100 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={70} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Line
                  type="monotone"
                  dataKey="value"
                  name="Food cost"
                  stroke={TONE_VAR.warn}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Most used ingredients" subtitle="By quantity consumed">
          <ChartFrame loading={loading} empty={ingredients.length === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ingredients.slice(0, 10).map((r) => ({
                  name: r.inventory_item_name,
                  value: Math.round(r.cost_paise / 100),
                }))}
                layout="vertical"
                margin={{ left: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Bar dataKey="value" name="Cost" fill={TONE_VAR.info} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>
      </div>

      <div className="mt-4 space-y-4">
        <Panel title="Ingredient consumption" subtitle={`${ingredients.length} ingredients`}>
          <DataTable
            rows={ingredients}
            columns={ingredientColumns}
            loading={loading}
            searchOn={(r) => r.inventory_item_name}
            exportName="ingredient-consumption"
            pageSize={12}
          />
        </Panel>

        <Panel title="Food cost by menu item" subtitle="Ingredient cost per portion">
          <DataTable
            rows={items}
            columns={itemColumns}
            loading={loading}
            searchOn={(r) => `${r.item_title} ${r.item_kind}`}
            exportName="food-cost-by-item"
            pageSize={12}
          />
        </Panel>

        <div className="grid gap-4 xl:grid-cols-2">
          <Panel title="Most used ingredients" subtitle="Top 10">
            <ul className="divide-y divide-border text-sm">
              {ingredients.slice(0, 10).map((r) => (
                <li key={r.inventory_item_id} className="flex items-center justify-between py-2">
                  <span className="flex items-center gap-2">
                    <ArrowUpWideNarrow className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    {r.inventory_item_name}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {num(r.quantity)} {r.unit} · {formatPaise(r.cost_paise)}
                  </span>
                </li>
              ))}
              {ingredients.length === 0 ? (
                <li className="py-4 text-muted-foreground">No consumption in this period.</li>
              ) : null}
            </ul>
          </Panel>
          <Panel title="Least used ingredients" subtitle="Bottom 10">
            <ul className="divide-y divide-border text-sm">
              {leastUsed.map((r) => (
                <li key={r.inventory_item_id} className="flex items-center justify-between py-2">
                  <span className="flex items-center gap-2">
                    <ArrowDownWideNarrow className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    {r.inventory_item_name}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {num(r.quantity)} {r.unit} · {formatPaise(r.cost_paise)}
                  </span>
                </li>
              ))}
              {leastUsed.length === 0 ? (
                <li className="py-4 text-muted-foreground">No consumption in this period.</li>
              ) : null}
            </ul>
          </Panel>
        </div>

        <Panel title="Top dishes by ingredient cost" subtitle="Food cost, selling price and margin">
          <DataTable
            rows={dishes}
            columns={dishColumns}
            loading={ex.isPending}
            searchOn={(r) => r.item_title}
            exportName="dish-food-cost"
            pageSize={12}
            minWidth={900}
          />
        </Panel>

        <div className="grid gap-4 xl:grid-cols-2">
          <Panel title="Most expensive ingredients" subtitle="By spend in this period">
            <DataTable
              rows={expensive}
              columns={expensiveColumns}
              loading={ex.isPending}
              searchOn={(r) => r.inventory_item_name}
              exportName="expensive-ingredients"
              pageSize={10}
            />
          </Panel>
          <Panel title="Waste analysis" subtitle="Waste, damage and expiry">
            <DataTable
              rows={waste}
              columns={wasteColumns}
              loading={ex.isPending}
              searchOn={(r) => r.inventory_item_name}
              exportName="waste-analysis"
              pageSize={10}
            />
          </Panel>
        </div>

        <Panel title="Low stock impact" subtitle="Ingredients at or below reorder level">
          <DataTable
            rows={lowStock}
            columns={lowStockColumns}
            loading={ex.isPending}
            searchOn={(r) => `${r.inventory_item_name} ${r.dishes.join(" ")}`}
            exportName="low-stock-impact"
            pageSize={10}
            minWidth={900}
          />
        </Panel>
      </div>

      <Dialog open={breakdown !== null} onOpenChange={(open) => !open && setBreakdown(null)}>
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>Recipe cost breakdown — {breakdown?.item_title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2">Ingredient</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Cost</th>
                  <th className="py-2 text-right">Contribution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {breakdownLines.map((l) => (
                  <tr key={l.id}>
                    <td className="py-2">{l.inventory_item_name}</td>
                    <td className="py-2 text-right tabular-nums">
                      {num(l.consumption_qty)} {l.stock_unit}
                    </td>
                    <td className="py-2 text-right tabular-nums">{formatPaise(l.line_cost_paise)}</td>
                    <td className="py-2 text-right tabular-nums">
                      {breakdownTotal > 0
                        ? `${Math.round((l.line_cost_paise / breakdownTotal) * 1000) / 10}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
                {breakdownLines.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-4 text-muted-foreground">
                      {recipeQ.isPending ? "Loading recipe…" : "No recipe lines."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
